import { useMutation } from '@apollo/react-hooks';
import { TFunction } from 'next-i18next';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useToast } from '~components/toast/use-toast';
import {
  AmendmentDetailOption,
  AmendmentDetailStatusOption,
  BookingLinkStatusTypeOption,
} from '~graphql/__generated__/globalTypes';
import { SP_AmendmentGetBooking_studentGetBooking } from '~graphql/__generated__/SP_AmendmentGetBooking';
import {
  SP_MutationConfirmAmendmentByReplacement,
  SP_MutationConfirmAmendmentByReplacementVariables,
} from '~graphql/__generated__/SP_MutationConfirmAmendmentByReplacement';
import {
  SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings,
  SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_bookingLink,
} from '~graphql/__generated__/SP_QueryStudentGetBookings';
import {
  SP_StudentGetBookingInfo_studentGetBooking,
  SP_StudentGetBookingInfo_studentGetBooking_bookingLink,
} from '~graphql/__generated__/SP_StudentGetBookingInfo';
import { MUTATION_CONFIRM_AMENDMENT_BY_REPLACEMENT } from '~graphql/amendment';
import { Router } from '~i18n';
import { displayWarnToast, modalDisappear } from '~store/modal/actions';
import { formatToUpper } from '~utils/js-helper';

export type bookingType =
  | SP_StudentGetBookingInfo_studentGetBooking
  | SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings
  | SP_AmendmentGetBooking_studentGetBooking
  | null
  | undefined;

export type bookingLinkType =
  | SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_bookingLink
  | SP_StudentGetBookingInfo_studentGetBooking_bookingLink
  | null
  | undefined;

type latestAmendmentDetailType = {
  targetBookingId: string | null;
  status: AmendmentDetailStatusOption | null;
  type: AmendmentDetailOption | null;
};

function useIsTakeoverPending(bookingLink: bookingLinkType) {
  return useMemo(() => {
    return bookingLink?.status === BookingLinkStatusTypeOption.PENDING;
  }, [bookingLink]);
}

function useTakeoverThroughOriginalBooking(booking: bookingType) {
  const { bookingLink } = booking || {};

  const isTakeoverPending = useIsTakeoverPending(bookingLink);
  return useMemo(() => {
    if (!isTakeoverPending) return;

    const { originalBooking } = bookingLink || {};
    return booking?.id === originalBooking?.id;
  }, [booking, bookingLink, isTakeoverPending]);
}

function useTakeoverThroughReplacementBooking(booking: bookingType) {
  const { bookingLink } = booking || {};

  const isTakeoverPending = useIsTakeoverPending(bookingLink);
  return useMemo(() => {
    if (!isTakeoverPending) return;

    const { replacementBooking } = bookingLink || {};
    return booking?.id === replacementBooking?.id;
  }, [booking, bookingLink, isTakeoverPending]);
}

function useTakeoverThroughOccupiedBooking(booking: bookingType) {
  const { bookingLink } = booking || {};
  const isTakeoverPending = useIsTakeoverPending(bookingLink);

  return useMemo(() => {
    if (!isTakeoverPending) return;
    const { occupiedBooking } = bookingLink || {};
    return booking?.id === occupiedBooking?.id;
  }, [booking, bookingLink, isTakeoverPending]);
}

function useTakeoverOccupiedBookingIsPending(booking: bookingType) {
  const { bookingLink } = booking || {};
  const isTakeoverThroughOccupiedBooking = useTakeoverThroughOccupiedBooking(booking);
  const isTakeoverPending = useIsTakeoverPending(bookingLink);

  return useMemo(() => {
    return isTakeoverPending && isTakeoverThroughOccupiedBooking;
  }, [isTakeoverPending, isTakeoverThroughOccupiedBooking]);
}

export const isTakeoverCreateReplacementBooking = (replacementBooking: any) =>
  !!replacementBooking?.id;

function useTakeoverConfirm(booking: bookingType, t: TFunction) {
  const { id, bookingLink } = booking || {};
  const dispatch = useDispatch();
  const { addToast } = useToast();

  const isTakeoverThroughOriginalBooking = useTakeoverThroughOriginalBooking(booking);
  const isTakeoverThroughOccupiedBooking = useTakeoverThroughOccupiedBooking(booking);

  const takeoverCreateReplacementBooking = isTakeoverCreateReplacementBooking(
    bookingLink?.replacementBooking
  );

  const [mutationConfirmAmendmentByReplacement, { loading }] = useMutation<
    SP_MutationConfirmAmendmentByReplacement,
    SP_MutationConfirmAmendmentByReplacementVariables
  >(MUTATION_CONFIRM_AMENDMENT_BY_REPLACEMENT, {
    onCompleted: data => {
      dispatch(modalDisappear());
      window.location.reload();
    },
    onError: ({ graphQLErrors }) => {
      const { message } = graphQLErrors[0] || {};
      addToast(
        t('error.message', {
          errorMsg: `(${formatToUpper(message)})`,
        })
      );
    },
  });

  if (takeoverCreateReplacementBooking) {
    if (isTakeoverThroughOriginalBooking) {
      return () =>
        Router.push(`/amendment-process?bookingId=${bookingLink?.replacementBooking?.id}`);
    }

    if (isTakeoverThroughOccupiedBooking) {
      return () => Router.push(`/amendment-process?bookingId=${id}`);
    }
  } else {
    if (isTakeoverThroughOriginalBooking) {
      return () =>
        dispatch(
          displayWarnToast({
            isOpen: true,
            isShowClose: true,
            title: t('takeover.confirm.title'),
            content: t('takeover.confirm.sub_title'),
            btnText: t('takeover.confirm.sure'),
            loading,
            onConfirm: () => {
              mutationConfirmAmendmentByReplacement({
                variables: {
                  bookingId: id || '',
                },
              });
            },
            onClose: () => {
              dispatch(modalDisappear());
            },
          })
        );
    }
    if (isTakeoverThroughOccupiedBooking) {
      return () => Router.push(`/amendment-process?bookingId=${id}`);
    }
  }
}

export {
  useIsTakeoverPending,
  useTakeoverConfirm,
  useTakeoverOccupiedBookingIsPending,
  useTakeoverThroughOccupiedBooking,
  useTakeoverThroughOriginalBooking,
  useTakeoverThroughReplacementBooking,
};
